import { useState, useMemo } from 'react';
import { usePermissions } from '../../hooks/usePermissions';
import { type ColumnDef } from '@tanstack/react-table';
import { Pencil, Trash2, Send, Pin, PinOff, Megaphone, Search } from 'lucide-react';
import { toast } from 'sonner';
import { useCrud } from '../../api/hooks/useCrud';
import { DataTable } from '../../components/ui/DataTable';
import { Modal } from '../../components/ui/Modal';
import { ConfirmModal } from '../../components/ui/ConfirmModal';
import { SetupPageHero } from '../../components/onboarding/SetupPageHero';
import type { Turma } from '../../types/models';
import '../../components/ui/datatable.css';

/** Alinhado ao modelo Prisma / `CreateComunicadoDto` (corpo, destinatario). */
interface Comunicado {
  id: string;
  titulo: string;
  corpo: string;
  tipo: string;
  destinatario: string;
  fixado: boolean;
  turmaId?: string;
  ativo: boolean;
  criadoEm: string;
}

/** `TipoComunicado` no backend */
const TIPOS = ['AVISO', 'URGENTE', 'EVENTO', 'REUNIAO', 'LEMBRETE'] as const;

/** `DestinatarioComunicado` no backend */
const DESTINATARIOS = ['TODOS', 'TURMA', 'PROFESSORES', 'RESPONSAVEIS'] as const;

const DESTINATARIO_LABEL: Record<string, string> = {
  TODOS: 'Todos',
  TURMA: 'Turma',
  PROFESSORES: 'Professores',
  RESPONSAVEIS: 'Responsáveis',
};

const TIPO_COLORS: Record<string, { bg: string; color: string }> = {
  AVISO:    { bg: '#fef3c7', color: '#d97706' },
  URGENTE:  { bg: '#fee2e2', color: '#dc2626' },
  EVENTO:   { bg: '#f3e8ff', color: '#7c3aed' },
  REUNIAO:  { bg: '#dcfce7', color: '#166534' },
  LEMBRETE: { bg: '#dbeafe', color: '#2563eb' },
};

interface ComunicadosPageProps {
  /** Destinatarios a esconder do select (ex.: professor nao pode enviar para PROFESSORES) */
  excludeDestinatarios?: string[];
  /** Professor: apenas visualiza o mural da escola */
  readOnly?: boolean;
}

export function ComunicadosPage({ excludeDestinatarios, readOnly }: ComunicadosPageProps = {}) {
  const { canManage, canView } = usePermissions();

  const { data, isLoading, create, update, deactivate } = useCrud<Comunicado>('comunicados');
  const allowedDestinatarios = excludeDestinatarios
    ? DESTINATARIOS.filter(d => !excludeDestinatarios.includes(d))
    : DESTINATARIOS;
  const { data: turmas } = useCrud<Turma>('turmas');
  const [searchQuery, setSearchQuery] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Comunicado | null>(null);
  const [form, setForm] = useState({
    titulo: '',
    corpo: '',
    tipo: 'AVISO',
    destinatario: 'TODOS' as (typeof DESTINATARIOS)[number],
    fixado: false,
    turmaId: '',
  });
  const [deleteTarget, setDeleteTarget] = useState<Comunicado | null>(null);

  const openNew = () => {
    setEditing(null);
    setForm({
      titulo: '',
      corpo: '',
      tipo: 'AVISO',
      destinatario: 'TODOS',
      fixado: false,
      turmaId: '',
    });
    setModalOpen(true);
  };

  const openEdit = (c: Comunicado) => {
    setEditing(c);
    setForm({
      titulo: c.titulo,
      corpo: c.corpo,
      tipo: TIPOS.includes(c.tipo as (typeof TIPOS)[number]) ? c.tipo : 'AVISO',
      destinatario: (DESTINATARIOS as readonly string[]).includes(c.destinatario) ? c.destinatario as (typeof DESTINATARIOS)[number] : 'TODOS',
      fixado: c.fixado || false,
      turmaId: c.turmaId || '',
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.titulo.trim() || !form.corpo.trim()) {
      toast.error('Preencha título e conteúdo.');
      return;
    }
    if (form.destinatario === 'TURMA' && !form.turmaId) {
      toast.error('Selecione uma turma quando o destinatário for “Turma”.');
      return;
    }
    const payload = {
      titulo: form.titulo.trim(),
      corpo: form.corpo.trim(),
      tipo: form.tipo,
      destinatario: form.destinatario,
      turmaId: form.turmaId || undefined,
      fixado: form.fixado,
    };
    try {
      if (editing) {
        await update.mutateAsync({ id: editing.id, ...payload });
        toast.success('Comunicado atualizado!');
      } else {
        await create.mutateAsync(payload);
        toast.success('Comunicado enviado!');
      }
      setModalOpen(false);
    } catch (e: unknown) {
      const d = e && typeof e === 'object' && 'response' in e
        ? (e as { response?: { data?: { message?: string; error?: { message?: string } } } }).response?.data
        : undefined;
      const msg = d?.error?.message ?? d?.message;
      toast.error(typeof msg === 'string' && msg ? msg : 'Erro ao salvar');
    }
  };
  const requestDelete = (c: Comunicado) => setDeleteTarget(c);
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const id = deleteTarget.id;
    setDeleteTarget(null);
    try { await deactivate.mutateAsync(id); toast.success('Removido'); } catch { toast.error('Erro'); }
  };
  const toggleFixar = async (c: Comunicado) => {
    try {
      await update.mutateAsync({ id: c.id, fixado: !c.fixado });
      toast.success(c.fixado ? 'Desfixado' : 'Fixado no mural!');
    } catch { toast.error('Erro'); }
  };

  const formatDate = (d: string) => { try { return new Date(d).toLocaleDateString('pt-BR'); } catch { return d; } };

  const filteredRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return data;
    return data.filter((c) =>
      c.titulo.toLowerCase().includes(q) ||
      c.tipo.toLowerCase().includes(q) ||
      c.destinatario.toLowerCase().includes(q) ||
      (c.corpo && c.corpo.toLowerCase().includes(q)),
    );
  }, [data, searchQuery]);

  // Mural fixado
  const fixados = data.filter(c => c.fixado && c.ativo);

  const columns: ColumnDef<Comunicado>[] = [
    {
      id: 'fixado', header: '', size: 30,
      cell: ({ row }) => row.original.fixado ? <Pin size={14} style={{ color: '#d97706' }} /> : null,
    },
    { accessorKey: 'titulo', header: 'Título' },
    { accessorKey: 'tipo', header: 'Tipo', cell: ({ row }) => {
      const c = TIPO_COLORS[row.original.tipo] || { bg: '#f3f4f6', color: '#6b7280' };
      return <span className="badge" style={{ background: c.bg, color: c.color }}>{row.original.tipo}</span>;
    }},
    {
      accessorKey: 'destinatario',
      header: 'Destinatário',
      cell: ({ row }) => (
        <span className="badge badge-active">{DESTINATARIO_LABEL[row.original.destinatario] ?? row.original.destinatario}</span>
      ),
    },
    { accessorKey: 'criadoEm', header: 'Data', cell: ({ row }) => formatDate(row.original.criadoEm) },
    ...(readOnly
      ? []
      : [
          {
            id: 'actions',
            header: '',
            cell: ({ row }: { row: { original: Comunicado } }) => (
              <div style={{ display: 'flex', gap: 4 }}>
                <button type="button" className="action-btn" title={row.original.fixado ? 'Desfixar' : 'Fixar'} onClick={() => toggleFixar(row.original)}>
                  {row.original.fixado ? <PinOff size={15} /> : <Pin size={15} />}
                </button>
                <button type="button" className="action-btn" title="Editar" onClick={() => openEdit(row.original)}><Pencil size={15} /></button>
                <button
                  type="button"
                  className="action-btn action-btn-danger"
                  title="Remover comunicado"
                  aria-label="Remover comunicado"
                  onClick={() => requestDelete(row.original)}
                >
                  <Trash2 size={15} />
                </button>
              </div>
            ),
          } as ColumnDef<Comunicado>,
        ]),
  ];

  const tipoLegenda = TIPOS.map((t) => ({
    tipo: t,
    count: data.filter((c) => c.tipo === t && c.ativo).length,
    ...(TIPO_COLORS[t] || { bg: '#f3f4f6', color: '#6b7280' }),
  }));

  return (
    <div className="admin-page" style={{ marginTop: -8, display: 'flex', flexDirection: 'column', paddingBottom: 48 }}>
      {!isLoading && (
        <SetupPageHero
          icon={Megaphone}
          etapa={7}
          totalEtapas={8}
          titulo="Comunicados"
          descricao={readOnly ? 'Mural oficial da escola (somente leitura)' : 'Envie avisos e comunicados para a comunidade escolar'}
          acoes={[]}
          dismissivel={false}
          compact
        />
      )}

      {data.length > 0 && (
      <div className="kpi-grid kpi-grid-5 kpi-grid-compact">
        {tipoLegenda.map((t) => (
          <div
            key={t.tipo}
            className="kpi-card"
            style={{
              background: t.bg,
              borderColor: `${t.color}33`,
            }}
          >
            <div className="kpi-card-info">
              <h3 style={{ color: t.color }}>{t.tipo}</h3>
              <span className="kpi-value" style={{ color: t.color }}>
                {t.count}
              </span>
            </div>
          </div>
        ))}
      </div>
      )}

      {fixados.length > 0 && (
        <div
          style={{
            background: '#fffbeb',
            borderRadius: 12,
            padding: 16,
            border: '1px solid #fde68a',
            marginBottom: 8,
          }}
        >
          <h3 style={{ fontSize: '0.85rem', fontWeight: 700, color: '#92400e', margin: '0 0 10px', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Pin size={16} /> Mural fixado
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {fixados.map((c) => (
              <div key={c.id} style={{ background: 'var(--bg-card, white)', borderRadius: 8, padding: '10px 14px', border: '1px solid #fde68a' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>{c.titulo}</span>
                  <span className="badge" style={{ ...(TIPO_COLORS[c.tipo] || { bg: '#f3f4f6', color: '#6b7280' }), fontSize: '0.65rem' }}>{c.tipo}</span>
                </div>
                <p style={{ fontSize: '0.78rem', color: '#6b7280', margin: '4px 0 0', lineHeight: 1.4 }}>
                  {c.corpo.slice(0, 120)}
                  {c.corpo.length > 120 ? '...' : ''}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {!isLoading && data.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <Megaphone size={48} style={{ color: 'var(--text-muted, #9ca3af)', marginBottom: 16, opacity: 0.5 }} />
          <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: 'var(--text-main, #1e293b)', margin: '0 0 8px' }}>
            Nenhum comunicado ainda
          </h3>
          <p style={{ fontSize: '.85rem', color: 'var(--text-muted, #6b7280)', maxWidth: 360, margin: '0 auto 20px', lineHeight: 1.5 }}>
            Crie o primeiro comunicado para informar a comunidade escolar sobre avisos, eventos e lembretes importantes.
          </p>
          {!readOnly && (
            <button type="button" className="btn-primary" onClick={openNew} style={{ height: 38, padding: '0 20px', fontSize: '.85rem', display: 'inline-flex', alignItems: 'center', gap: 8, borderRadius: 10 }}>
              <Send size={16} /> Novo comunicado
            </button>
          )}
        </div>
      ) : (
      <div
        className="ac-admin-data-card"
        style={{
          background: 'var(--bg-card, white)',
          border: '1px solid var(--border-color, #e5e7eb)',
          borderRadius: 12,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
        }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', borderBottom: '1px solid var(--border-color, #e5e7eb)', background: 'var(--bg-main, #f8fafc)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <div style={{ position: 'relative' }}>
              <Search size={14} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
              <input
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar comunicado..."
                title="Buscar comunicado"
                style={{ height: 34, width: 280, borderRadius: 8, border: '1px solid var(--border-color, #e5e7eb)', padding: '0 10px 0 30px', fontSize: '.82rem', background: 'var(--bg-card)', color: 'var(--text-main)' }}
              />
            </div>
            {!readOnly && (
              <button type="button" className="btn-primary" onClick={openNew} style={{ height: 34, padding: '0 12px', fontSize: '.8rem', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Send size={15} /> Novo comunicado
              </button>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>{filteredRows.length} registro(s)</span>
          </div>
        </div>

        <div className="ac-admin-list-scroll">
          <DataTable columns={columns} data={filteredRows} isLoading={isLoading} searchPlaceholder="Buscar comunicado..." showToolbar={false} />
        </div>
      </div>
      )}

      {!readOnly && <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Editar comunicado' : 'Novo comunicado'}
        contentClassName="ac-comunicado-modal"
        footer={
          <>
            <button type="button" className="btn-cancel" onClick={() => setModalOpen(false)}>Cancelar</button>
            <button type="button" className="btn-save" onClick={() => void handleSave()}>{editing ? 'Salvar' : 'Enviar'}</button>
          </>
        }
      >
        <div className="form-group">
          <label className="form-label" htmlFor="comunicado-titulo">Título *</label>
          <input
            id="comunicado-titulo"
            className="form-input"
            value={form.titulo}
            onChange={(e) => setForm({ ...form, titulo: e.target.value })}
            placeholder="Título do comunicado"
            autoComplete="off"
          />
        </div>
        <div className="ac-comunicado-modal-grid">
          <div className="form-group">
            <label className="form-label" htmlFor="comunicado-tipo">Tipo *</label>
            <select id="comunicado-tipo" className="form-select" value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })}>
              {TIPOS.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="comunicado-destinatario">Destinatário *</label>
            <select
              id="comunicado-destinatario"
              className="form-select"
              value={form.destinatario}
              onChange={(e) => {
                const destinatario = e.target.value as (typeof DESTINATARIOS)[number];
                setForm((prev) => ({
                  ...prev,
                  destinatario,
                  turmaId: destinatario !== 'TURMA' ? '' : prev.turmaId,
                }));
              }}
            >
              {allowedDestinatarios.map((d) => (
                <option key={d} value={d}>{DESTINATARIO_LABEL[d]}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="ac-comunicado-modal-grid">
          <div className="form-group">
            <label className="form-label" htmlFor="comunicado-turma">
              Turma {form.destinatario === 'TURMA' ? '*' : '(opcional)'}
            </label>
            <select
              id="comunicado-turma"
              className="form-select"
              value={form.turmaId}
              onChange={(e) => setForm({ ...form, turmaId: e.target.value })}
            >
              <option value="">{form.destinatario === 'TURMA' ? 'Selecione a turma…' : 'Todas'}</option>
              {turmas.filter((t) => t.ativo).map((t) => (
                <option key={t.id} value={t.id}>{t.nome}</option>
              ))}
            </select>
          </div>
          <div className="ac-comunicado-modal-fixar">
            <input type="checkbox" id="fixado" checked={form.fixado} onChange={(e) => setForm({ ...form, fixado: e.target.checked })} />
            <label htmlFor="fixado">Fixar no mural</label>
          </div>
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="comunicado-corpo">Conteúdo *</label>
          <textarea
            id="comunicado-corpo"
            className="form-input"
            value={form.corpo}
            onChange={(e) => setForm({ ...form, corpo: e.target.value })}
            rows={4}
            placeholder="Escreva o comunicado…"
          />
        </div>
      </Modal>}

      {!readOnly && (
      <ConfirmModal
        open={!!deleteTarget}
        title="Remover comunicado"
        message={deleteTarget ? `Deseja remover o comunicado "${deleteTarget.titulo}"? Ele deixa de aparecer na lista (desativação).` : ''}
        confirmLabel="Remover"
        variant="danger"
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => void confirmDelete()}
      />
      )}
    </div>
  );
}
